import React, { useState, useEffect } from "react";
import { useLocation, useRouteMatch } from "react-router";
import { Organization } from "../models/Organization";
import { PermitRegister } from "../models/PermitRegister";
import { useLazyGetMyRegistersByOrganizationId } from "../operations/queries/getMyRegistersByOrganizationId";
import { useLazyGetOrganizationConfiguration } from "../operations/queries/getOrganizationConfiguration";
import { AppConfiguration } from "../models/AppConfiguration";
import { OrgUser } from "../models/OrgUser";
import { useGetMyActiveOrgUsers } from "../operations/queries/getMyActiveOrgUsers";
import { ApplicationPaths } from "../components/api-authorization/ApiAuthorizationConstants";
import { Project } from "../models/Project";
import { useLazyGetProject } from "../operations/queries/getProject";

interface Props {
  children: React.ReactNode;
}

export const AppContext = React.createContext<{
  currentOrganization?: Organization;
  currentOrgUser?: OrgUser;
  myOrganizations?: Organization[];
  myProjects?: Project[];
  myRegisters?: PermitRegister[];
  organizationName: string;
  currentProject?: Project;
  projectName: string;
  currentPermitRegister?: PermitRegister;
  permitRegisterTitle?: string;
  organizationConfiguration: AppConfiguration | undefined;
  sitePhotoMaxSizeByte: number | undefined;
  documentMaxSizeByte: number | undefined;
}>({
  currentOrganization: undefined,
  currentOrgUser: undefined,
  myOrganizations: undefined,
  myProjects: undefined,
  myRegisters: undefined,
  organizationName: "",
  currentProject: undefined,
  projectName: "",
  currentPermitRegister: undefined,
  permitRegisterTitle: "",
  organizationConfiguration: undefined,
  sitePhotoMaxSizeByte: undefined,
  documentMaxSizeByte: undefined,
});

export const AppContextProvider = ({ children }: Props) => {
  const location = useLocation();
  const isAuthPage = location.pathname.startsWith(ApplicationPaths.ApiAuthorizationPrefix);

  const { data: myActiveOrgUsers } = useGetMyActiveOrgUsers(isAuthPage);
  const { getMyRegistersByOrganizationId: getMyRegisters, data: myRegistersData } =
    useLazyGetMyRegistersByOrganizationId();
  const { getOrganizationConfiguration, data: organizationConfiguration } = useLazyGetOrganizationConfiguration();
  const { getProject, data: projectData } = useLazyGetProject();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | undefined>(undefined);
  const [currentOrgUser, setCurrentOrgUser] = useState<OrgUser | undefined>(undefined);
  const [myRegisters, setMyRegisters] = useState<PermitRegister[] | undefined>(undefined);
  const [currentOrganizationConfiguration, setCurrentOrganizationConfiguration] = useState<
    AppConfiguration | undefined
  >(undefined);
  const [myOrganizations, setMyOrganizations] = useState<Organization[] | undefined>(undefined);

  let orgNameMatch: any = useRouteMatch("/:orgName/");
  let projNameMatch: any = useRouteMatch("/:orgName/projects/:projectName/");
  let permitRegisterMatch: any = useRouteMatch("/:orgName/projects/:projectName/registers/:registerTitle/");

  const orgName = orgNameMatch?.params.orgName;
  const projName = projNameMatch?.params.projectName;
  const permitRegisterTitle = permitRegisterMatch?.params.registerTitle;

  // Derive myProjects as unique projects from myRegisters
  const myProjects = myRegisters
    ? Array.from(new Map(myRegisters.filter((r) => r.project != null).map((r) => [r.project!.id, r.project!])).values())
    : undefined;

  // Find matching project from myProjects by route param
  const matchedProject = projName ? myProjects?.find((p) => p.name === projName) : undefined;

  // Fetch full project (with boundaries) when matched project changes
  useEffect(() => {
    if (matchedProject) {
      getProject({ variables: { id: matchedProject.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getProject, matchedProject?.id]);

  // Derive currentProject from the separate getProject query (includes boundaries)
  const currentProject: Project | undefined = projectData?.project ? { ...projectData.project } : undefined;

  // Derive currentPermitRegister directly from myRegisters by route param
  const currentPermitRegister = permitRegisterTitle
    ? myRegisters?.find((r) => r.project?.name === projName && r.title === permitRegisterTitle)
    : undefined;

  // Set my Organizations
  useEffect(() => {
    if (myActiveOrgUsers && myActiveOrgUsers.myActiveOrgUsers) {
      setMyOrganizations(myActiveOrgUsers.myActiveOrgUsers.map((ou) => ou.organization));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myActiveOrgUsers?.myActiveOrgUsers]);

  // Set Current Organization
  useEffect(() => {
    if (myOrganizations && orgName) {
      var org = myOrganizations.find((o) => o.name === orgName);
      setCurrentOrganization(org);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myOrganizations, orgName]);

  // Set Current OrgUser
  useEffect(() => {
    if (myActiveOrgUsers && myActiveOrgUsers.myActiveOrgUsers && currentOrganization) {
      setCurrentOrgUser(myActiveOrgUsers.myActiveOrgUsers.find((ou) => ou.organization?.id === currentOrganization.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myActiveOrgUsers?.myActiveOrgUsers, currentOrganization]);

  // Fetch My Registers when organization changes
  useEffect(() => {
    if (currentOrganization) {
      getMyRegisters({ variables: { organizationId: currentOrganization.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMyRegisters, currentOrganization?.id]);

  // Set My Registers
  useEffect(() => {
    if (myRegistersData?.myRegistersByOrganizationId) {
      setMyRegisters(myRegistersData.myRegistersByOrganizationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myRegistersData?.myRegistersByOrganizationId]);

  useEffect(() => {
    if (currentOrganization) {
      getOrganizationConfiguration({ variables: { organizationId: currentOrganization.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id, getOrganizationConfiguration]);

  useEffect(() => {
    if (organizationConfiguration && organizationConfiguration.organizationConfiguration) {
      setCurrentOrganizationConfiguration(organizationConfiguration.organizationConfiguration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationConfiguration?.organizationConfiguration]);

  return (
    <AppContext.Provider
      value={{
        currentOrganization,
        currentOrgUser,
        myOrganizations,
        myProjects,
        myRegisters,
        organizationName: orgName,
        currentProject,
        projectName: projName,
        currentPermitRegister,
        permitRegisterTitle: permitRegisterTitle,
        organizationConfiguration: currentOrganizationConfiguration,
        sitePhotoMaxSizeByte:
          Number(currentOrganizationConfiguration?.system?.userFileMaxSizeMB.sitePhoto) * 1024 * 1024,
        documentMaxSizeByte: Number(currentOrganizationConfiguration?.system?.userFileMaxSizeMB.document) * 1024 * 1024,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
